import _ from 'lodash'
import { EntityState } from '@reduxjs/toolkit'

import { CommonState, Model } from '@/core/classes/Model'
import { SearchMeetingLinksQuery, SearchRequestOptions } from '@/core/types/request'
import { SearchResponse } from '@/core/types/response'
import { Period } from '@/core/types/shared'
import { PaymentConfig } from './payment.model'

import { SpecialityOption } from './speciality-option.model'
import { Staff } from './staff.model'
import { WaitingRoom } from './waiting-room.model'

export type ImmediateConsultationConfig = {
  enabled: boolean
  maxWaitingPatients?: number | null
  requirePaymentUpfront: boolean
  price?: number | null
  paymentReceiver?: string | null
}

export type MeetingLink = {
  _id: string
  availabilities: Availability[]
  busyEnabled?: boolean
  busySlots: Array<{ start: string; end: string }>
  createdBy: string
  description?: string
  isPrioritized?: boolean
  maxDays: number
  name: string
  slug: string
  specialities: Array<Pick<SpecialityOption, 'code' | 'name'> & { price: number }>
  staffs: Staff[]
  immediateConsultationConfig?: ImmediateConsultationConfig
  waitingRoom?: string | WaitingRoom
}

export type MeetingLinkStatistics = {
  pendingBulkOrders?: number
  pendingOrders: number
  totalMeetingLinks: number
  totalOrders: number
}

export type CreateMeetingLinkDto = {
  createdBy?: string
  description?: string
  name: string
  specialities: MeetingLink['specialities']
  staffs: string[]
  immediateConsultationConfig?: ImmediateConsultationConfig
  waitingRoom?: string
}

export type UpdateMeetingLinkDto = {
  description?: string
  isPrioritized?: boolean
  name?: string
  specialities?: MeetingLink['specialities']
  staffs?: string[]
  immediateConsultationConfig?: ImmediateConsultationConfig
  waitingRoom?: string
}

export type Unavailability = {
  _id: string
  startDate: string
  endDate: string
  startTime?: number
  endTime?: number
  reason?: string
  createdAt: string
}

export type Availability = {
  _id: string
  address: {
    description: string
    placeId: string
  } | null
  additionalAddress?: string
  avgMeetingDuration: number
  formTemplate?: string
  fromLink: MeetingLink | string
  isRecurrence: boolean
  meetingPlace: AvailabilityMeetingPlace
  notes: string
  recurrenceDays: Array<{ day: number; periods: Period[]; timeZone: string }>
  scheduledDays: Array<{ day: string; format: string; periods: Period[]; timeZone: string }>
  specialities: string[]
  paymentConfig?: PaymentConfig
  serviceArea?: {
    allowedPostalCodes?: string[]
    center?: { lat?: number; lng?: number; description?: string; placeId?: string } | null
    radiusKm?: number
  } | null
  unavailabilities?: Unavailability[]
}

export enum AvailabilityMeetingPlace {
  CENTER = 'CENTER',
  AT_HOME = 'AT_HOME',
  VISIO_CONFERENCE = 'VISIO_CONFERENCE',
  SECOND_OPINION = 'SECOND_OPINION'
}

export type CreateAvailabilityDto = Omit<Availability, '_id' | 'fromLink' | 'bulkorders'> & {
  formTemplate?: string
}

export type UpdateAvailabilityDto = Partial<CreateAvailabilityDto> & {}

export enum BulkOrdersStatus {
  Pending = 'pending',
  Accepted_BY_PDS = 'accepted',
  Accepted_BY_PDS_AA = 'accepted_by_pds_and_auto_accept',
  Done = 'done',
}

export type PatientInfo = {
  firstName: string
  lastName: string
  phone: string
  email: string
}

export type MeetingLinkState = CommonState & {
  meetingLinks: EntityState<MeetingLink, MeetingLink['_id']>
  availabilities: EntityState<Availability, Availability['_id']>
}

class MeetingLinkModel extends Model {
  static instance: MeetingLinkModel

  static getInstance() {
    if (!MeetingLinkModel.instance) {
      MeetingLinkModel.instance = new MeetingLinkModel('meetingLink')
    }
    return MeetingLinkModel.instance
  }

  getInitialState(): MeetingLinkState {
    return _.merge({}, this.commonState, {
      meetingLinks: {
        entities: {},
        ids: [],
      },
      availabilities: {
        entities: {},
        ids: [],
      },
    })
  }

  createMeetingLink = async (createMeetingLinkDto: CreateMeetingLinkDto) => {
    return await this.httpClient.request<MeetingLink>(`/meeting-links`, 'POST', createMeetingLinkDto)
  }

  getMeetingLink = async (id: string) => {
    return await this.httpClient.request<MeetingLink>(`/meeting-links/${id}`, 'GET')
  }

  getMeetingLinkByName = async (name: string) => {
    const meetingLinks = await this.httpClient.request<MeetingLink[]>(`/public/meeting-links`, 'GET', { name })
    return _.first(meetingLinks)
  }

  getMeetingLinks = async (query: SearchMeetingLinksQuery = {}) => {
    return await this.httpClient.request<MeetingLink[]>('/meeting-links', 'GET', query)
  }

  getStatistics = async () => {
    return await this.httpClient.request<MeetingLinkStatistics>('/meeting-links/statistics', 'GET')
  }

  listMeetingLinks = async (query: SearchMeetingLinksQuery, options?: SearchRequestOptions): Promise<SearchResponse<MeetingLink>> => {
    const defaultOptions = { limit: 5, order: 'ASC', skip: 0 }
    return await this.httpClient.request<SearchResponse<MeetingLink>>('/meeting-links/query', 'POST', { query, options: _.merge({}, defaultOptions, options) })
  }

  updateMeetingLink = async (meetingLinkId: string, updateMeetingLinkDto: UpdateMeetingLinkDto) => {
    return await this.httpClient.request<MeetingLink>(`/meeting-links/${meetingLinkId}`, 'PATCH', updateMeetingLinkDto)
  }

  createAvailability = async (meetingId: string, createAvailabilityDto: CreateAvailabilityDto) => {
    return await this.httpClient.request<MeetingLink>(`/meeting-links/${meetingId}/availabilities`, 'POST', createAvailabilityDto)
  }

  updateAvailability = async (meetingLinkId: string, availabilityId: string, createAvailabilityDto: UpdateAvailabilityDto) => {
    return await this.httpClient.request<MeetingLink>(`/meeting-links/${meetingLinkId}/availabilities/${availabilityId}`, 'PATCH', createAvailabilityDto)
  }

  deleteAvailability = async (meetingId: string, availabilityId: string) => {
    return await this.httpClient.request<MeetingLink>(`/meeting-links/${meetingId}/availabilities/${availabilityId}`, 'DELETE')
  }
}

export default MeetingLinkModel.getInstance()
